import { useState, useEffect, useCallback } from "react"
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Button,
  List,
  Avatar,
  Tag,
  Spin,
  message,
  DatePicker,
  Steps,
  Result,
  Divider,
  Empty
} from "antd"
import {
  ShopOutlined,
  CheckCircleOutlined,
  SendOutlined,
  TeamOutlined
} from "@ant-design/icons"
import { detectSimilarProviders, createQuoteRequest } from "../../services/QuoteRequestServices"

const { TextArea } = Input
const { Step } = Steps

export const QuoteRequestModal = ({
  visible,
  onCancel,
  product,
  getToken,
  onSuccess
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [providers, setProviders] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [requestSent, setRequestSent] = useState(false)
  const [sentCount, setSentCount] = useState(0)
  const [form] = Form.useForm()
  const productId = product?.id

  const detectProviders = useCallback(async () => {
    if (!productId) {
      setProviders([])
      return
    }

    setLoading(true)
    try {
      const token = await getToken()
      const result = await detectSimilarProviders(token, productId, 15)

      if (result.success && result.data?.providers) {
        setProviders(result.data.providers)
        if (result.data.providers.length > 0) {
          setCurrentStep(1)
        }
      } else {
        setProviders([])
      }
    } catch (error) {
      console.error("Error detecting providers:", error)
      message.error(error.message || "Error al detectar proveedores similares")
      setProviders([])
    } finally {
      setLoading(false)
    }
  }, [getToken, productId])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (visible && productId) {
      setCurrentStep(0)
      setProviders([])
      setRequestSent(false)
      setSentCount(0)
      form.resetFields()
      detectProviders()
    }
  }, [visible, productId, form, detectProviders])

  const handleSubmit = async (values) => {
    if (providers.length === 0) {
      message.warning("No hay proveedores para enviar la solicitud")
      return
    }

    setSubmitting(true)
    try {
      const token = await getToken()

      const result = await createQuoteRequest(
        token,
        product.id,
        values.specifications || "",
        values.quantity || 1,
        values.deliveryDeadline ? values.deliveryDeadline.toISOString() : null,
        values.notes || ""
      )

      if (result.success) {
        setRequestSent(true)
        setSentCount(result.data?.detected_providers?.length || providers.length)
        setCurrentStep(2)
        message.success(`Solicitud enviada a ${providers.length} proveedores`)
      }
    } catch (error) {
      console.error("Error creating quote request:", error)
      message.error(error.message || "Error al crear solicitud de cotización")
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (requestSent) {
      onSuccess?.()
    }
    onCancel()
  }

  const getDetectionMethodTag = (method) => {
    const methods = {
      category: { color: "blue", label: "Categoría" },
      materials: { color: "green", label: "Materiales" },
      vector: { color: "purple", label: "Similar" },
      hybrid: { color: "orange", label: "Híbrido" }
    }
    const info = methods[method] || { color: "default", label: method }
    return <Tag color={info.color}>{info.label}</Tag>
  }

  const renderStep0 = () => (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
      <Spin size="large" />
      <p style={{ marginTop: 16, color: "#666" }}>
        Buscando proveedores que venden productos similares...
      </p>
    </div>
  )

  const renderStep1 = () => (
    <div>
      {providers.length === 0 ? (
        <Empty
          description="No se encontraron proveedores con productos similares"
          style={{ padding: "40px 0" }}
        />
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <Tag icon={<TeamOutlined />} color="blue" style={{ fontSize: 14, padding: "4px 12px" }}>
              {providers.length} proveedores encontrados
            </Tag>
          </div>

          <List
            itemLayout="horizontal"
            dataSource={providers}
            style={{ maxHeight: 200, overflowY: "auto", marginBottom: 16 }}
            renderItem={(provider) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar icon={<ShopOutlined />} style={{ backgroundColor: "#1890ff" }} />}
                  title={
                    <span>
                      {provider.provider_name || provider.provider_company || "Proveedor"}
                      {" "}
                      {getDetectionMethodTag(provider.detection_method)}
                    </span>
                  }
                  description={
                    <span style={{ fontSize: 12 }}>
                      {provider.matching_product_count} producto(s) similar(es) |
                      Score: {(provider.similarity_score * 100).toFixed(0)}%
                    </span>
                  }
                />
              </List.Item>
            )}
          />

          <Divider />

          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
          >
            <Form.Item
              name="quantity"
              label="Cantidad requerida"
              rules={[{ required: true, message: "Ingrese la cantidad" }]}
              initialValue={1}
            >
              <InputNumber
                min={1}
                style={{ width: "100%" }}
                placeholder="Cantidad de unidades"
              />
            </Form.Item>

            <Form.Item
              name="deliveryDeadline"
              label="Fecha límite de entrega (opcional)"
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Seleccione fecha"
              />
            </Form.Item>

            <Form.Item
              name="specifications"
              label="Especificaciones (opcional)"
            >
              <TextArea
                rows={3}
                placeholder="Describa especificaciones adicionales del producto que necesita..."
              />
            </Form.Item>

            <Form.Item
              name="notes"
              label="Notas adicionales (opcional)"
            >
              <TextArea
                rows={2}
                placeholder="Cualquier información adicional para los proveedores..."
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<SendOutlined />}
                block
                size="large"
              >
                Enviar solicitud a {providers.length} proveedores
              </Button>
            </Form.Item>
          </Form>
        </>
      )}
    </div>
  )

  const renderStep2 = () => (
    <Result
      status="success"
      icon={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
      title="Solicitud Enviada"
      subTitle={
        <div>
          <p>Tu solicitud de cotización fue enviada a <strong>{sentCount}</strong> proveedores.</p>
          <p style={{ color: "#666", fontSize: 13 }}>
            Recibirás notificaciones por email cuando los proveedores respondan con sus cotizaciones.
          </p>
        </div>
      }
      extra={[
        <Button type="primary" key="close" onClick={handleClose}>
          Entendido
        </Button>
      ]}
    />
  )

  const renderContent = () => {
    if (loading) return renderStep0()
    if (requestSent) return renderStep2()
    return renderStep1()
  }

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <TeamOutlined />
          <span>Solicitar Cotizaciones de Proveedores Similares</span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      {product && (
        <div style={{
          background: "#f5f5f5",
          padding: "12px 16px",
          borderRadius: 8,
          marginBottom: 16
        }}>
          <p style={{ margin: 0, fontWeight: 500 }}>
            Producto de referencia: {product.name}
          </p>
          <p style={{ margin: 0, fontSize: 12, color: "#666" }}>
            Categoría: {product.category}
          </p>
        </div>
      )}

      {!loading && !requestSent && providers.length > 0 && (
        <Steps current={currentStep} size="small" style={{ marginBottom: 24 }}>
          <Step title="Detectando" />
          <Step title="Configurar" />
          <Step title="Enviado" />
        </Steps>
      )}

      {renderContent()}
    </Modal>
  )
}

export default QuoteRequestModal
